// Scheda pezzo condivisa (scansione + dettaglio carico): ristampa, modifica, uscita, taglia
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Printer, Pencil, PackageMinus, History, Scissors } from "lucide-react";
import { wmsApi, openWmsPdf, fmtKg, fmtDate, fmtDateTime } from "./wmsApi";
import { WButton, WInput, WField, WBadge, WModal } from "./WmsUi";
import { printPieces } from "./printTarget";

const PhotoThumb = ({ path }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true;
    wmsApi.get("/wms/photos", { params: { path }, responseType: "blob" })
      .then((r) => { if (alive) setUrl(URL.createObjectURL(r.data)); })
      .catch(() => {});
    return () => { alive = false; };
  }, [path]);
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noreferrer" data-testid="piece-photo-thumb">
      <img src={url} alt="etichetta fornitore" className="h-20 border-2 border-zinc-300 hover:border-zinc-900" />
    </a>
  );
};

const REASONS = [
  { value: "vendita", label: "Vendita" },
  { value: "scarto", label: "Scarto" },
  { value: "autoconsumo", label: "Autoconsumo" },
  { value: "lavorazione", label: "Lavorazione" },
  { value: "altro", label: "Altro" },
];

export default function WmsPieceCard({ data, onClose, onChanged }) {
  const { piece, lot, receipt } = data;
  const origin = piece.origin_override || lot?.origin || {};
  const [editOpen, setEditOpen] = useState(false);
  const [outOpen, setOutOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [form, setForm] = useState({
    weight_kg: piece.weight_kg, units: piece.units || 1, brand: piece.brand || "",
    origin: { birth: origin.birth || "", rearing: origin.rearing || "", slaughter: origin.slaughter || "", slaughter_code: origin.slaughter_code || "", cutting: origin.cutting || "", cutting_code: origin.cutting_code || "" },
  });
  const [outReason, setOutReason] = useState("vendita");
  const [outNote, setOutNote] = useState("");
  const [cutOpen, setCutOpen] = useState(false);
  const [boot, setBoot] = useState(null);
  const [cutQ, setCutQ] = useState("");
  const [cutForm, setCutForm] = useState({ product_id: "", weight_kg: "", brand: piece.brand || "" });
  const [cutResult, setCutResult] = useState(null);

  useEffect(() => {
    if (cutOpen && !boot) wmsApi.get("/wms/bootstrap").then((r) => setBoot(r.data)).catch(() => {});
  }, [cutOpen, boot]);

  const cutMatches = useMemo(() => {
    if (!boot || cutQ.length < 2) return [];
    const s = cutQ.toLowerCase();
    return boot.products.filter((p) => `${p.code} ${p.name} ${p.category_name}`.toLowerCase().includes(s)).slice(0, 6);
  }, [boot, cutQ]);
  const cutProd = boot?.products.find((p) => p.id === cutForm.product_id);

  const doCut = async () => {
    const w = parseFloat(String(cutForm.weight_kg).replace(",", "."));
    if (!cutForm.product_id) { toast.error("Scegli l'articolo del taglio"); return; }
    if (!w || w <= 0 || w >= piece.weight_kg) { toast.error(`Peso taglio non valido (max ${fmtKg(piece.weight_kg)} kg)`); return; }
    try {
      const r = await wmsApi.post(`/wms/pieces/${piece.id}/cut`, { product_id: cutForm.product_id, weight_kg: w, brand: cutForm.brand });
      setCutResult(r.data);
      toast.success(`Taglio creato: ${r.data.child_lot.product_name} ${fmtKg(r.data.child.weight_kg)} kg — il pezzo scende a ${fmtKg(r.data.parent.weight_kg)} kg`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore taglio"); }
  };

  const reprint = async (ids) => {
    const res = await printPieces(ids);
    if (res.mode === "station") toast.success(`Etichetta inviata a "${res.name}"`);
  };

  const save = async () => {
    try {
      await wmsApi.put(`/wms/pieces/${piece.id}`, { ...form, weight_kg: parseFloat(form.weight_kg), units: parseFloat(form.units) || 1 });
      toast.success("Pezzo aggiornato — ristampa l'etichetta");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore aggiornamento"); }
  };

  const consume = async () => {
    try {
      await wmsApi.post(`/wms/pieces/${piece.id}/consume`, { reason: outReason, note: outNote });
      toast.success("Pezzo scaricato dal magazzino");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore uscita"); }
  };

  return (
    <WModal open onClose={onClose} title={`Pezzo #${piece.number} · ${piece.code}`} wide>
      <div className="space-y-4" data-testid="piece-card">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-bold text-lg">{lot?.product_name}</p>
          <WBadge tone="dark">{lot?.category_name || "—"}</WBadge>
          {piece.brand && <WBadge tone="orange">{piece.brand}</WBadge>}
          <WBadge tone={piece.status === "in_stock" ? "green" : "red"}>{piece.status === "in_stock" ? "In giacenza" : `Uscito: ${piece.out_reason || ""}`}</WBadge>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white border-2 border-zinc-900 p-3 text-center">
            <p className="font-['Chivo'] font-black text-2xl">{fmtKg(piece.weight_kg)}</p>
            <p className="text-[10px] uppercase font-bold text-zinc-500">kg</p>
          </div>
          <div className="bg-white border border-zinc-300 p-3 text-center">
            <p className="font-['Chivo'] font-black text-2xl">{piece.units || 1}</p>
            <p className="text-[10px] uppercase font-bold text-zinc-500">pezzi</p>
          </div>
          <div className="bg-white border border-zinc-300 p-3 text-center">
            <p className="font-bold text-sm mt-1">{lot?.lot_supplier || lot?.internal_lot || "—"}</p>
            <p className="text-[10px] uppercase font-bold text-zinc-500">lotto</p>
          </div>
          <div className="bg-white border border-zinc-300 p-3 text-center">
            <p className="font-bold text-sm mt-1">{fmtDate(lot?.expiry_date)}</p>
            <p className="text-[10px] uppercase font-bold text-zinc-500">scadenza</p>
          </div>
        </div>
        <div className="text-sm text-zinc-600 space-y-0.5">
          {origin.birth && <p>Nato: <b>{origin.birth}</b> · Allevato: <b>{origin.rearing || "—"}</b></p>}
          {(origin.slaughter || origin.slaughter_code) && <p>Macellato: <b>{origin.slaughter} {origin.slaughter_code}</b></p>}
          {(origin.cutting || origin.cutting_code) && <p>Sezionato: <b>{origin.cutting} {origin.cutting_code}</b></p>}
          {piece.cut_from_piece && <p>Tagliato da pezzo: <b className="font-mono">{piece.cut_from_piece}</b></p>}
          <p>Carico: <b>{receipt?.number}</b> · {receipt?.supplier_name} · DDT {receipt?.ddt_number || "—"} · Etichetta stampata {piece.printed || 0} volte</p>
        </div>
        {piece.photo_path && <PhotoThumb path={piece.photo_path} />}

        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <WButton variant="dark" onClick={() => reprint([piece.id])} data-testid="piece-reprint-btn"><Printer className="w-5 h-5" /> Ristampa</WButton>
          <WButton onClick={() => setEditOpen(true)} data-testid="piece-edit-btn"><Pencil className="w-5 h-5" /> Modifica</WButton>
          {piece.status === "in_stock" && (
            <WButton onClick={() => setCutOpen(!cutOpen)} data-testid="piece-cut-btn" className="!border-[#FF4500] !text-[#E03C00]"><Scissors className="w-5 h-5" /> Taglia</WButton>
          )}
          {piece.status === "in_stock" && (
            <WButton variant="danger" onClick={() => setOutOpen(true)} data-testid="piece-out-btn"><PackageMinus className="w-5 h-5" /> Uscita</WButton>
          )}
          {(piece.history || []).length > 0 && (
            <WButton onClick={() => setHistOpen(!histOpen)} data-testid="piece-history-btn"><History className="w-5 h-5" /> Storico ({piece.history.length})</WButton>
          )}
        </div>

        {cutOpen && !cutResult && (
          <div className="border-2 border-[#FF4500] bg-white p-4 space-y-3" data-testid="cut-panel">
            <p className="font-bold uppercase text-sm text-[#E03C00]">Taglia — crea un derivato (tracciabilità ereditata, peso per differenza)</p>
            {cutProd ? (
              <div className="flex items-center gap-2 flex-wrap">
                <WBadge tone="green">{cutProd.name}</WBadge>
                <WBadge tone="dark">{cutProd.category_name || "—"}</WBadge>
                <button className="text-xs font-bold uppercase text-red-600" onClick={() => setCutForm({ ...cutForm, product_id: "" })}>Cambia</button>
              </div>
            ) : (
              <div>
                <WInput placeholder="Cerca articolo del taglio (es. testa di filetto)..." value={cutQ} onChange={(e) => setCutQ(e.target.value)} data-testid="cut-search" />
                {cutMatches.map((p) => (
                  <button key={p.id} onClick={() => setCutForm({ ...cutForm, product_id: p.id })} className="w-full text-left px-3 h-11 bg-white border border-zinc-200 hover:bg-orange-50 text-sm font-semibold flex items-center gap-2" data-testid={`cut-opt-${p.code}`}>
                    <span className="font-mono text-xs text-zinc-400">{p.code}</span> {p.name} <WBadge tone="dark">{p.category_name || "—"}</WBadge>
                  </button>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <WField label={`Peso taglio kg (max ${fmtKg(piece.weight_kg)})`}>
                <WInput type="number" step="0.001" inputMode="decimal" value={cutForm.weight_kg} onChange={(e) => setCutForm({ ...cutForm, weight_kg: e.target.value })} placeholder="es. 2,500" data-testid="cut-weight" />
              </WField>
              <WField label="Brand del taglio">
                <WInput value={cutForm.brand} onChange={(e) => setCutForm({ ...cutForm, brand: e.target.value })} data-testid="cut-brand" />
              </WField>
            </div>
            <WButton variant="primary" className="w-full" onClick={doCut} data-testid="cut-confirm-btn"><Scissors className="w-5 h-5" /> Conferma taglio</WButton>
          </div>
        )}

        {cutResult && (
          <div className="border-2 border-green-600 bg-green-50 p-4 space-y-3" data-testid="cut-result">
            <p className="font-bold text-green-800">
              ✓ Taglio fatto: <b>{cutResult.child_lot.product_name}</b> {fmtKg(cutResult.child.weight_kg)} kg (lotto {cutResult.child_lot.internal_lot})
              — il pezzo originale ora pesa <b>{fmtKg(cutResult.parent.weight_kg)} kg</b>
            </p>
            <div className="grid grid-cols-2 gap-2">
              <WButton variant="dark" onClick={() => reprint([cutResult.parent.id])} data-testid="cut-print-parent"><Printer className="w-4 h-4" /> Etichetta pezzo ({fmtKg(cutResult.parent.weight_kg)} kg)</WButton>
              <WButton variant="dark" onClick={() => reprint([cutResult.child.id])} data-testid="cut-print-child"><Printer className="w-4 h-4" /> Etichetta taglio ({fmtKg(cutResult.child.weight_kg)} kg)</WButton>
            </div>
            <WButton variant="success" className="w-full !h-12" onClick={() => onChanged?.()} data-testid="cut-done-btn">Fatto</WButton>
          </div>
        )}

        {histOpen && (
          <div className="bg-white border border-zinc-300 divide-y divide-zinc-100 text-xs" data-testid="piece-history">
            {piece.history.map((h, i) => (
              <div key={i} className="px-3 py-2 flex gap-2 flex-wrap">
                <span className="text-zinc-400">{fmtDateTime(h.ts)}</span>
                <b className="uppercase">{h.field}</b>
                <span>{typeof h.old === "object" ? JSON.stringify(h.old) : String(h.old ?? "—")} → <b>{typeof h.new === "object" ? JSON.stringify(h.new) : String(h.new ?? "—")}</b></span>
                <span className="text-zinc-400">({h.user})</span>
              </div>
            ))}
          </div>
        )}

        {editOpen && (
          <div className="border-2 border-zinc-900 bg-white p-4 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <WField label="Peso kg"><WInput type="number" step="0.001" inputMode="decimal" value={form.weight_kg} onChange={(e) => setForm({ ...form, weight_kg: e.target.value })} data-testid="edit-weight" /></WField>
              <WField label="Pezzi"><WInput type="number" step="1" value={form.units} onChange={(e) => setForm({ ...form, units: e.target.value })} data-testid="edit-units" /></WField>
              <WField label="Brand"><WInput value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} data-testid="edit-brand" /></WField>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <WField label="Nato in"><WInput value={form.origin.birth} onChange={(e) => setForm({ ...form, origin: { ...form.origin, birth: e.target.value } })} data-testid="edit-birth" /></WField>
              <WField label="Allevato in"><WInput value={form.origin.rearing} onChange={(e) => setForm({ ...form, origin: { ...form.origin, rearing: e.target.value } })} data-testid="edit-rearing" /></WField>
              <WField label="Macellato in"><WInput value={form.origin.slaughter} onChange={(e) => setForm({ ...form, origin: { ...form.origin, slaughter: e.target.value } })} data-testid="edit-slaughter" /></WField>
              <WField label="Bollo macello"><WInput value={form.origin.slaughter_code} onChange={(e) => setForm({ ...form, origin: { ...form.origin, slaughter_code: e.target.value } })} data-testid="edit-slaughter-code" /></WField>
              <WField label="Sezionato in"><WInput value={form.origin.cutting} onChange={(e) => setForm({ ...form, origin: { ...form.origin, cutting: e.target.value } })} data-testid="edit-cutting" /></WField>
              <WField label="Bollo sezionamento"><WInput value={form.origin.cutting_code} onChange={(e) => setForm({ ...form, origin: { ...form.origin, cutting_code: e.target.value } })} data-testid="edit-cutting-code" /></WField>
            </div>
            <WButton variant="primary" className="w-full" onClick={save} data-testid="edit-save-btn">Salva modifiche</WButton>
          </div>
        )}

        {outOpen && (
          <div className="border-2 border-red-600 bg-white p-4 space-y-3" data-testid="out-panel">
            <p className="font-bold uppercase text-sm text-red-700">Uscita dal magazzino — motivo:</p>
            <div className="grid grid-cols-3 gap-2">
              {REASONS.map((r) => (
                <button key={r.value} onClick={() => setOutReason(r.value)} data-testid={`out-reason-${r.value}`}
                  className={`h-14 font-bold uppercase text-sm border-2 ${outReason === r.value ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-500 border-zinc-300"}`}>
                  {r.label}
                </button>
              ))}
            </div>
            <WInput value={outNote} onChange={(e) => setOutNote(e.target.value)} placeholder="Nota (opzionale)" data-testid="out-note" />
            <WButton variant="danger" className="w-full !bg-red-600 !text-white !border-red-600" onClick={consume} data-testid="out-confirm-btn">Conferma uscita</WButton>
          </div>
        )}
      </div>
    </WModal>
  );
}
